import { motion } from 'framer-motion'

import type { Bracket, BracketMatch, BracketTeam } from '@/lib/api/schemas'

/**
 * Tournament Stadium — big VS card for the current focus match.
 *
 * Picks the most interesting match from the bracket and renders it
 * at broadcast scale. Priority order:
 *   1. IN_PROGRESS (a match currently being played)
 *   2. AWAITING_CONFIRMATION (winner claimed, awaiting opponent agreement)
 *   3. SCHEDULED + soonest scheduledAt
 *   4. Any PENDING with both participants set
 *
 * Sits in the LIVE / FINAL_MATCH phase composition. The keyed `motion`
 * wrapper at the top of the card means each new focus match triggers
 * the slide-in animation per the Emil framework: 320ms ease-out,
 * 10% viewport-width slide distance, restraint over richness.
 */
export function MatchFocusCard({ bracket }: { bracket: Bracket | null }) {
  const focus = pickFocusMatch(bracket)
  if (!focus) {
    return (
      <div className="flex h-full items-center justify-center text-center">
        <p
          className="font-display font-medium uppercase"
          style={{
            color: 'var(--tv-fg-subtle)',
            letterSpacing: '0.3em',
            fontSize: 'clamp(1rem, 1.2vw, 1.3rem)',
          }}
        >
          No active match
        </p>
      </div>
    )
  }
  const totalRounds = countWinnersRounds(bracket)
  return (
    <FocusCardBody
      match={focus}
      bestOf={bracket?.bestOf ?? focus.bestOf ?? 1}
      totalRounds={totalRounds}
    />
  )
}

/** Distinct round positions across the winners-bracket matches —
 *  matches the `groupByRound` filter in StadiumBracket so labels stay
 *  consistent. Returns null when we can't compute (no bracket / no
 *  WB matches), in which case FocusHeader falls back to "Round N". */
function countWinnersRounds(bracket: Bracket | null): number | null {
  if (!bracket?.matches) return null
  const winners = bracket.matches.filter((m) => m.kind === 'W' || m.kind === 'GF')
  if (winners.length === 0) return null
  const rounds = new Set(winners.map((m) => m.round))
  return rounds.size
}

function FocusCardBody({
  match,
  bestOf,
  totalRounds,
}: {
  match: BracketMatch
  bestOf: number
  totalRounds: number | null
}) {
  const a = match.participantA
  const b = match.participantB
  const winnerId = match.winnerTeamId
  const status = (match.status ?? 'PENDING').toUpperCase()

  return (
    <motion.div
      // Keyed by matchId so a focus change unmounts/remounts the card,
      // triggering the slide-in. The 120px translateX is ~6% of a
      // 1920px viewport — restraint per the Emil framework
      // (occasional-frequency moment, not rare).
      key={match.matchId}
      initial={{ opacity: 0, transform: 'translateX(120px) scale(0.96)' }}
      animate={{ opacity: 1, transform: 'translateX(0px) scale(1)' }}
      transition={{ duration: 0.32, ease: [0.23, 1, 0.32, 1] }}
      className="flex h-full flex-col justify-center gap-[clamp(20px,3vh,40px)] px-[clamp(20px,3vw,48px)]"
    >
      <FocusHeader match={match} bestOf={bestOf} totalRounds={totalRounds} />
      <div className="flex items-center justify-between gap-[clamp(20px,3vw,48px)]">
        <TeamBlock team={a} side="left" winner={winnerId === a?.teamId} loser={!!winnerId && winnerId !== a?.teamId} />
        <VsBadge status={status} />
        <TeamBlock team={b} side="right" winner={winnerId === b?.teamId} loser={!!winnerId && winnerId !== b?.teamId} />
      </div>
      <FocusFooter match={match} />
    </motion.div>
  )
}

function FocusHeader({
  match,
  bestOf,
  totalRounds,
}: {
  match: BracketMatch
  bestOf: number
  /** Total winners-bracket round count. Lets us label by round-from-end
   *  (Final / Semifinal / Quarterfinal) instead of the less-readable
   *  "Round N" when the size is known. */
  totalRounds: number | null
}) {
  const round = typeof match.round === 'number' ? match.round : null
  const matchIndex = typeof match.matchIndex === 'number' ? match.matchIndex : null
  const kindLabel = roundLabel(match)
  const parts: string[] = []
  if (kindLabel) {
    parts.push(kindLabel)
  } else if (round != null && totalRounds != null && totalRounds > 0) {
    parts.push(roundLabelFromPosition(round, totalRounds))
  } else if (round != null) {
    parts.push(`Round ${round + 1}`)
  }
  if (matchIndex != null) parts.push(`Match ${matchIndex + 1}`)
  if (bestOf > 1) parts.push(`Best of ${bestOf}`)
  return (
    <p
      className="font-display text-center font-extrabold uppercase"
      style={{
        color: 'var(--tv-primary)',
        letterSpacing: '0.32em',
        fontSize: 'clamp(0.9rem, 1.15vw, 1.25rem)',
      }}
    >
      {parts.join('  ·  ')}
    </p>
  )
}

/** Mirrors the column-header logic in StadiumBracket so the focus
 *  card and the bracket pane agree on what each round is called. */
function roundLabelFromPosition(round: number, totalRounds: number): string {
  const fromEnd = totalRounds - 1 - round
  if (fromEnd === 0) return 'Final'
  if (fromEnd === 1) return 'Semifinal'
  if (fromEnd === 2) return 'Quarterfinal'
  if (fromEnd === 3) return 'Round of 16'
  if (fromEnd === 4) return 'Round of 32'
  return `Round ${round + 1}`
}

function TeamBlock({
  team,
  side,
  winner,
  loser,
}: {
  team: BracketTeam | null | undefined
  side: 'left' | 'right'
  winner: boolean
  loser: boolean
}) {
  const name = teamDisplayName(team)
  const initials = teamInitials(team)
  const avatarUrl = teamAvatar(team)
  // Side-coded brand colour — primary blue on left, secondary purple
  // on right. Matches broadcast-style "home / away" colour convention
  // so spectators instantly read which side is which. Winner override
  // (green) wins over the side colour because the result is the news.
  const sideColour = side === 'left' ? 'var(--tv-primary)' : 'var(--tv-secondary)'
  const sideGlow = side === 'left'
    ? 'rgba(61, 111, 255, 0.55)'
    : 'rgba(217, 70, 239, 0.55)'
  const ringColour = winner ? 'var(--tv-live)' : sideColour
  const ringGlow = winner ? 'rgba(0, 255, 136, 0.55)' : sideGlow
  return (
    <div
      className="flex flex-1 flex-col items-center gap-[clamp(10px,1.6vh,24px)]"
      style={{
        opacity: loser ? 0.4 : 1,
        transition: 'opacity 240ms ease',
      }}
    >
      {/* Side label — small label above the avatar, in the side colour.
          Adds at-distance read of "left side / right side" without
          needing to recognise the avatar. */}
      <span
        className="font-display font-extrabold uppercase"
        style={{
          color: ringColour,
          letterSpacing: '0.3em',
          fontSize: 'clamp(0.7rem, 0.85vw, 1rem)',
        }}
      >
        {side === 'left' ? 'Side A' : 'Side B'}
      </span>
      <div className="relative">
        {/* Outer glow ring — purely decorative, sits behind the avatar
            and gives it a luminous halo at TV viewing distance. */}
        <span
          aria-hidden
          className="absolute -inset-3 rounded-3xl blur-xl"
          style={{
            backgroundColor: ringGlow,
            opacity: loser ? 0 : 0.5,
            transition: 'opacity 240ms ease',
          }}
        />
        <div
          className="relative flex items-center justify-center overflow-hidden rounded-3xl"
          style={{
            width: 'clamp(140px, 13vw, 540px)',
            height: 'clamp(140px, 13vw, 540px)',
            backgroundColor: 'var(--tv-bg-surface)',
            border: `clamp(4px, 0.32vw, 7px) solid ${ringColour}`,
            boxShadow: `0 0 80px -10px ${ringGlow}, 0 14px 50px -18px rgba(0,0,0,0.7)`,
            transition: 'border-color 240ms ease, box-shadow 240ms ease',
          }}
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <span
              className="font-display font-black"
              style={{
                color: ringColour,
                fontSize: 'clamp(2.5rem, 4vw, 5rem)',
                letterSpacing: '-0.04em',
              }}
            >
              {initials}
            </span>
          )}
        </div>
      </div>
      <div className="flex max-w-full flex-col items-center gap-1.5">
        <span
          className="font-display max-w-full truncate font-extrabold tracking-tight"
          style={{
            color: 'var(--tv-fg)',
            fontSize: 'clamp(1.5rem, 2.4vw, 2.6rem)',
            textAlign: 'center',
          }}
        >
          {name}
        </span>
        {team?.teamLabel && team.members && team.members.length > 1 && (
          <span
            className="font-display font-medium uppercase"
            style={{
              color: 'var(--tv-fg-subtle)',
              letterSpacing: '0.18em',
              fontSize: 'clamp(0.7rem, 0.85vw, 0.9rem)',
            }}
          >
            {team.members.length}-player squad
          </span>
        )}
      </div>
    </div>
  )
}

function VsBadge({ status }: { status: string }) {
  const live = status === 'IN_PROGRESS' || status === 'AWAITING_CONFIRMATION'
  // Two diagonal stripes flanking the "VS" — one blue (side A), one
  // purple (side B). Reads as a broadcast-style "clash" composition
  // rather than a flat letter. Live state lights the dot underneath.
  return (
    <div className="flex shrink-0 flex-col items-center gap-[clamp(10px,1.4vh,22px)]">
      <div className="relative flex items-center gap-[clamp(8px,0.8vw,16px)]">
        <span
          aria-hidden
          className="block"
          style={{
            width: 'clamp(28px, 2.4vw, 56px)',
            height: 'clamp(4px, 0.35vw, 8px)',
            backgroundColor: 'var(--tv-primary)',
            transform: 'skewX(-30deg)',
            boxShadow: '0 0 16px -2px rgba(61,111,255,0.55)',
          }}
        />
        <span
          className="font-display font-black tracking-tight"
          style={{
            color: live ? 'var(--tv-live)' : 'var(--tv-fg)',
            fontSize: 'clamp(3.5rem, 5.5vw, 7rem)',
            letterSpacing: '-0.06em',
            textShadow: live
              ? '0 0 32px rgba(0,255,136,0.45)'
              : '0 4px 24px rgba(0,0,0,0.6)',
          }}
        >
          VS
        </span>
        <span
          aria-hidden
          className="block"
          style={{
            width: 'clamp(28px, 2.4vw, 56px)',
            height: 'clamp(4px, 0.35vw, 8px)',
            backgroundColor: 'var(--tv-secondary)',
            transform: 'skewX(-30deg)',
            boxShadow: '0 0 16px -2px rgba(217,70,239,0.55)',
          }}
        />
      </div>
      {live ? (
        <span
          className="font-display inline-flex items-center gap-2 rounded-full border px-[clamp(10px,1vw,18px)] py-[clamp(4px,0.5vh,8px)] font-extrabold uppercase"
          style={{
            borderColor: 'var(--tv-live)',
            color: 'var(--tv-live)',
            backgroundColor: 'rgba(0,255,136,0.10)',
            letterSpacing: '0.28em',
            fontSize: 'clamp(0.7rem, 0.85vw, 0.95rem)',
          }}
        >
          <span
            className="stadium-live-dot inline-block rounded-full"
            aria-hidden
            style={{
              width: 'clamp(7px, 0.55vw, 10px)',
              height: 'clamp(7px, 0.55vw, 10px)',
              backgroundColor: 'var(--tv-live)',
            }}
          />
          On now
        </span>
      ) : null}
    </div>
  )
}

function FocusFooter({ match }: { match: BracketMatch }) {
  const status = (match.status ?? 'PENDING').toUpperCase()
  const scheduledAt = toMs(match.scheduledAt)
  if (status === 'IN_PROGRESS') {
    return <FooterLine label="LIVE NOW" color="var(--tv-live)" />
  }
  if (status === 'AWAITING_CONFIRMATION') {
    return <FooterLine label="Awaiting result confirmation" color="var(--tv-gold)" />
  }
  if (status === 'DISPUTED') {
    return <FooterLine label="Result disputed" color="var(--tv-secondary)" />
  }
  if (status === 'SCHEDULED' && scheduledAt != null) {
    return <FooterLine label={`Scheduled ${formatWhen(scheduledAt)}`} color="var(--tv-fg-muted)" />
  }
  if (status === 'PROPOSED' && scheduledAt != null) {
    return <FooterLine label={`Proposed ${formatWhen(scheduledAt)}`} color="var(--tv-fg-muted)" />
  }
  return <FooterLine label="Awaiting schedule" color="var(--tv-fg-subtle)" />
}

function FooterLine({ label, color }: { label: string; color: string }) {
  // Pill treatment — outlined chip in the status colour so the
  // line carries weight at TV viewing distance instead of reading
  // as muted footer copy.
  return (
    <div className="flex justify-center">
      <span
        className="font-display rounded-full border px-[clamp(16px,1.4vw,28px)] py-[clamp(6px,0.7vh,12px)] font-extrabold uppercase"
        style={{
          borderColor: color,
          color,
          backgroundColor: `color-mix(in oklab, ${color} 10%, transparent)`,
          letterSpacing: '0.28em',
          fontSize: 'clamp(0.9rem, 1.1vw, 1.2rem)',
          boxShadow: `0 0 24px -6px ${color}`,
        }}
      >
        {label}
      </span>
    </div>
  )
}

// ─── helpers ────────────────────────────────────────────────────────

function pickFocusMatch(bracket: Bracket | null): BracketMatch | null {
  if (!bracket?.matches) return null
  const incomplete = bracket.matches.filter(
    (m) => (m.status ?? '').toUpperCase() !== 'COMPLETE' &&
           (m.status ?? '').toUpperCase() !== 'COMPLETED' &&
           (m.status ?? '').toUpperCase() !== 'OVERRIDDEN',
  )
  if (incomplete.length === 0) return null

  const inProgress = incomplete.find((m) => (m.status ?? '').toUpperCase() === 'IN_PROGRESS')
  if (inProgress) return inProgress

  const awaiting = incomplete.find((m) => (m.status ?? '').toUpperCase() === 'AWAITING_CONFIRMATION')
  if (awaiting) return awaiting

  const scheduled = incomplete
    .filter((m) => (m.status ?? '').toUpperCase() === 'SCHEDULED' || (m.status ?? '').toUpperCase() === 'PROPOSED')
    .filter((m) => m.participantA && m.participantB)
    .sort((a, b) => (toMs(a.scheduledAt) ?? Infinity) - (toMs(b.scheduledAt) ?? Infinity))
  if (scheduled.length > 0) return scheduled[0]

  const ready = incomplete.find((m) => m.participantA && m.participantB)
  if (ready) return ready

  return incomplete[0]
}

function roundLabel(match: BracketMatch): string | null {
  switch (match.kind) {
    case 'GF': return 'Grand Final'
    case 'BR': return 'Bronze Match'
    case 'L':  return 'Lower Bracket'
    case 'R':  return 'Repechage'
    default:   return null
  }
}

function teamDisplayName(team: BracketTeam | null | undefined): string {
  if (!team) return 'TBD'
  if (team.teamLabel?.trim()) return team.teamLabel
  const members = team.members ?? []
  if (members.length === 1) return members[0].displayName ?? members[0].callsign ?? 'Player'
  if (members.length > 0) return members[0].displayName ?? 'Team'
  return 'Team'
}

function teamInitials(team: BracketTeam | null | undefined): string {
  if (!team) return '?'
  const name = teamDisplayName(team)
  const trimmed = name.trim()
  if (!trimmed) return '?'
  const parts = trimmed.split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return trimmed.slice(0, 2).toUpperCase()
}

function teamAvatar(team: BracketTeam | null | undefined): string | null {
  if (!team) return null
  const members = team.members ?? []
  if (members.length === 1 && members[0].avatarUrl) return members[0].avatarUrl
  return null
}

function toMs(v: number | string | null | undefined): number | null {
  if (v == null) return null
  if (typeof v === 'number') return v
  const parsed = Date.parse(v)
  return Number.isFinite(parsed) ? parsed : null
}

function formatWhen(ts: number): string {
  const diff = ts - Date.now()
  if (diff <= 0) return 'now'
  const minutes = Math.round(diff / 60_000)
  if (minutes < 60) return `in ${minutes}m`
  const hours = Math.floor(minutes / 60)
  const remMins = minutes % 60
  return remMins === 0 ? `in ${hours}h` : `in ${hours}h ${remMins}m`
}
